import React from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ProtectedRoute } from '@/components/auth/protected-route';
import { RequireResource } from '@/components/auth/require-resource';
import { AppShell } from '@/components/layout/app-shell';
import { Toaster } from '@/components/ui/toast';
import { ConfirmProvider } from '@/hooks/use-confirm';
import { LoginPage } from '@/pages/auth/login';
import { MagicLinkSentPage } from '@/pages/auth/magic-link-sent';
import { MagicLinkVerifyPage } from '@/pages/auth/magic-link-verify';
import { ForgotPasswordPage } from '@/pages/auth/forgot-password';
import { ResetPasswordPage } from '@/pages/auth/reset-password';
import { DashboardPage } from '@/pages/dashboard';
import { WidgetPage } from '@/pages/widget';
import { EmployeesPage } from '@/pages/employees';
import { RolesPage } from '@/pages/roles';
import { RoleDetailPage } from '@/pages/roles/detail';
import { MasterDataPage } from '@/pages/master-data';
import { WeightraxReviewPage } from '@/pages/weightrax-review';
import { FleetPage, DispatchPage, ServicingPage } from '@/pages/fleet-ops';
import { JobCardServicePage } from '@/pages/fleet-ops/job-card-service';
import { OnboardWizardPage } from '@/pages/fleet-ops/onboard-wizard';
import { AssetTransferWizardPage } from '@/pages/fleet-ops/transfer-wizard';
import { JobCardDashboardPage } from '@/pages/fleet-ops/job-card-dashboard';
import { SyncRunsListPage, SyncRunDetailPage } from '@/pages/fleet-ops/sync-runs';
import { ProfilePage } from '@/pages/profile';
import { AssetTrackerPage } from '@/pages/vehicles/asset-tracker';
import { ServiceDashboardPage } from '@/pages/servicing/dashboard';
import { WofRenewFormPage, LicenceRenewFormPage } from '@/pages/compliance/renew-form';
import { RegistrationRemindersPage, WofRemindersPage, CofRemindersPage } from '@/pages/compliance/reminder-report';
import {
  PeoplePage, FleetExtrasPage, DispatchExtrasPage,
  ServicingExtrasPage, OperationsPage, IntegrationPage, CompliancePage, AuditPage,
} from '@/pages/extras';
import { SettingsPage } from '@/pages/admin/settings';
import { DeveloperPage } from '@/pages/admin/developer';
import { DeveloperNewKeyPage } from '@/pages/admin/developer-new-key';
import { DeveloperEditKeyPage } from '@/pages/admin/developer-edit-key';
import { RunSheetPrintPage } from '@/pages/dispatch/run-sheet-print';
import { MyDispatchPage } from '@/pages/my-dispatch';
import { MyDispatchDetailPage } from '@/pages/my-dispatch/detail';
import { NotificationsPage } from '@/pages/notifications';
import { useDefaultHomePath } from '@/hooks/use-default-home';
import { usePushRouting } from '@/features/notifications/use-push-routing';
import { useNotificationToasts } from '@/features/notifications/use-notification-toasts';
import { registerQueryClientForDataSync } from '@/lib/data-sync';

/** Sends users hitting `/` to the first sidebar entry they can actually
 *  access (per their role). Keeps non-admin users out of an Access-denied
 *  bounce on /dashboard. */
function HomeRedirect() {
  const home = useDefaultHomePath();
  return <Navigate to={home} replace />;
}

function PushRoutingMount() {
  usePushRouting();
  useNotificationToasts();
  return null;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});
// Every successful write through lib/api.ts now refreshes the affected query
// families in this tab and broadcasts to sibling tabs (see lib/data-sync.ts).
registerQueryClientForDataSync(queryClient);

// Injected by webpack DefinePlugin (see webpack.config.ts). True only for the
// native-app-shell embedded build, where the app is served from inside an
// iframe at /hosts/atlas/index.html with no server-side SPA fallback — so
// path-based routing would 404 on reload. HashRouter keeps all routing in the
// URL fragment. The web/Render build keeps BrowserRouter for clean URLs.
declare const __EMBEDDED__: boolean;
const Router = typeof __EMBEDDED__ !== 'undefined' && __EMBEDDED__ ? HashRouter : BrowserRouter;

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ConfirmProvider>
      <Router>
        <Routes>
          <Route path="/auth/login" element={<LoginPage />} />
          <Route path="/auth/magic-link-sent" element={<MagicLinkSentPage />} />
          <Route path="/auth/verify" element={<MagicLinkVerifyPage />} />
          <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/reset-password" element={<ResetPasswordPage />} />

          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <AppShell>
                  <PushRoutingMount />
                  <Routes>
                    <Route path="/" element={<HomeRedirect />} />
                    <Route path="/dashboard" element={<RequireResource slug="dashboard"><DashboardPage /></RequireResource>} />
                    <Route path="/widget" element={<RequireResource slug="widget_page"><WidgetPage /></RequireResource>} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/notifications" element={<NotificationsPage />} />
                    <Route path="/employees" element={<RequireResource slug="employees_page"><EmployeesPage /></RequireResource>} />
                    <Route path="/roles" element={<RequireResource slug="roles_page"><RolesPage /></RequireResource>} />
                    <Route path="/roles/:id" element={<RequireResource slug="roles_page"><RoleDetailPage /></RequireResource>} />
                    <Route path="/admin/settings" element={<RequireResource slug="settings_page"><SettingsPage /></RequireResource>} />
                    {/* Developer — API keys for machine-to-machine integrations.
                        Its own slug rather than `settings_page`, which also gates
                        Email/SMS history: reading those shouldn't imply the right
                        to mint credentials. */}
                    <Route path="/admin/developer" element={<RequireResource slug="api_keys"><DeveloperPage /></RequireResource>} />
                    <Route path="/admin/developer/new" element={<RequireResource slug="api_keys"><DeveloperNewKeyPage /></RequireResource>} />
                    <Route path="/admin/developer/:id/edit" element={<RequireResource slug="api_keys"><DeveloperEditKeyPage /></RequireResource>} />
                    <Route path="/master-data/customers/review" element={<RequireResource slug="customers_page"><WeightraxReviewPage kind="customer" /></RequireResource>} />
                    <Route path="/master-data/:entity" element={<MasterDataPage />} />
                    {/* The service-execution page is the technician's
                        workspace — they don't have access to the Job Cards
                        report (`job_cards_page`), but they do have view on
                        the Servicing Report (`services_page`). Gate this
                        route on the latter so the "Start Service" deep-link
                        from the Servicing Report doesn't bounce them. */}
                    <Route path="/fleet/job-cards/:id/service" element={<RequireResource slug="services_page"><JobCardServicePage /></RequireResource>} />
                    <Route path="/fleet-extras/onboard-assets/:id/wizard" element={<RequireResource slug="asset_onboarding_page"><OnboardWizardPage /></RequireResource>} />
                    <Route path="/fleet-extras/asset-transfers/:id/wizard" element={<RequireResource slug="asset_transfer_page"><AssetTransferWizardPage /></RequireResource>} />
                    <Route path="/job-cards/dashboard" element={<RequireResource slug="job_card_dashboard_page"><JobCardDashboardPage /></RequireResource>} />
                    <Route path="/vehicles/tracker" element={<RequireResource slug="assets_page"><AssetTrackerPage /></RequireResource>} />
                    <Route path="/servicing/dashboard" element={<RequireResource slug="service_dashboard_page"><ServiceDashboardPage /></RequireResource>} />
                    <Route path="/compliance/wof-renew" element={<RequireResource slug="wof_renewals_page"><WofRenewFormPage /></RequireResource>} />
                    <Route path="/compliance/license-renew" element={<RequireResource slug="license_renewals_page"><LicenceRenewFormPage /></RequireResource>} />
                    <Route path="/compliance/reminders/registration" element={<RegistrationRemindersPage />} />
                    <Route path="/compliance/reminders/wof" element={<WofRemindersPage />} />
                    <Route path="/compliance/reminders/cof" element={<CofRemindersPage />} />
                    <Route path="/my-dispatch" element={<RequireResource slug="my_dispatch_page"><MyDispatchPage /></RequireResource>} />
                    <Route path="/my-dispatch/:id" element={<RequireResource slug="my_dispatch_page"><MyDispatchDetailPage /></RequireResource>} />
                    <Route path="/sync-runs" element={<SyncRunsListPage />} />
                    <Route path="/sync-runs/:id" element={<SyncRunDetailPage />} />
                    <Route path="/fleet/:entity" element={<FleetPage />} />
                    {/* Specific dispatch routes need to win against the
                        catch-all below so each gets its own permission gate.
                        React Router v6 matches by specificity then order, so
                        these literal-segment routes take priority. */}
                    <Route path="/dispatch/schedule" element={<RequireResource slug="dispatch_dashboard_page"><DispatchPage entityOverride="schedule" /></RequireResource>} />
                    <Route path="/dispatch/jobs" element={<RequireResource slug="dispatch_page"><DispatchPage entityOverride="jobs" /></RequireResource>} />
                    <Route path="/dispatch/:entity" element={<DispatchPage />} />
                    <Route path="/servicing/:entity" element={<ServicingPage />} />
                    <Route path="/people/:entity" element={<PeoplePage />} />
                    <Route path="/fleet-extras/:entity" element={<FleetExtrasPage />} />
                    <Route path="/dispatch-extras/run-sheets/:id/print" element={<RunSheetPrintPage />} />
                    <Route path="/dispatch-extras/:entity" element={<DispatchExtrasPage />} />
                    <Route path="/servicing-extras/:entity" element={<ServicingExtrasPage />} />
                    <Route path="/operations/catalogue-items/review" element={<RequireResource slug="catalogue_items_page"><WeightraxReviewPage kind="product" /></RequireResource>} />
                    <Route path="/operations/:entity" element={<OperationsPage />} />
                    <Route path="/integration/:entity" element={<IntegrationPage />} />
                    <Route path="/compliance/:entity" element={<CompliancePage />} />
                    <Route path="/audit/:entity" element={<AuditPage />} />
                    <Route path="*" element={<div className="p-8"><h1 className="text-xl">404 — Not Found</h1></div>} />
                  </Routes>
                </AppShell>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Router>
      <Toaster />
      </ConfirmProvider>
    </QueryClientProvider>
  );
}
